"use client"

import type React from "react"

import axios from "axios"
import { ChevronDown, Download, Shield } from "lucide-react"
import { useSnackbar } from "notistack"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Document, Page, pdfjs } from 'react-pdf'
import useWindowSize from "src/adapters/primary/libs/useWindowSize"
import client from "src/adapters/secondary/libs/client"
import { Button } from "src/components/ui/button"
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface CguModalProps {
  isOpen: boolean;
  onAccept: (cguId: number) => void
}

export default function CguModal({ isOpen, onAccept }: CguModalProps) {
  const [cguPdf, setCguPdf] = useState(false);
  const [cgu, setCgu] = useState<{ url: string, id: any }>({ url: "", id: undefined })
  const [hasScrolled, setHasScrolled] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [numPages, setNumPages] = useState(null);
  const { width } = useWindowSize();
  const documentWidth = width ? width < 640 ? width - 24 : 848 : 848
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const onDocumentLoadSuccess = ({ numPages }: { numPages: any }) => {
    setNumPages(numPages);
  };
  useEffect(() => {
    if (isOpen && !cguPdf) {
      getPDF()
    }
  }, [])
  useEffect(() => {
    getPDF()
  }, [isOpen, !cguPdf])

  async function getPDF() {
    try {
      const { data: cgu } = await client.get(`/cgu/last-cgu`);
      const { data: pdf } = await axios(cgu.url, {
        method: 'GET',
        responseType: 'blob',
      });
      setCgu(cgu)
      setCguPdf(cgu.url !== "")
    } catch (e) {
      enqueueSnackbar(t("text.error") + '. ' + t('text.pleaseTryAgain'), {
        autoHideDuration: 7000,
        variant: 'error'
      });
    }
  };



  const downloadCgu = () => {
    if (cgu) {
      window.open(cgu.url, "_blank");
    }
  };


  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement
    const scrollTop = target.scrollTop
    const scrollHeight = target.scrollHeight
    const clientHeight = target.clientHeight

    // User has scrolled down
    if (scrollTop > 50) {
      setHasScrolled(true)
    }

    // Check if user is near the bottom (within 50px)
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setIsAtBottom(true)
    }
  }

  if (!isOpen) return null
  console.log({ width })
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl mx-4 bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 p-6 border-b border-gray-200">
          <div className="flex items-center justify-center min-w-10 h-10 bg-emerald-100 rounded-lg">
            <Shield className="min-w-5 size-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t("words.cgu")}</h2>
            <p className="text-sm text-gray-500">{t("text.readAllAndAcceptCgu")}</p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="w-full h-0">
          {!hasScrolled && (
            <div className="w-full flex justify-center items-center">
              <div className="relative w-max top-4 z-10 bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg animate-bounce">
                <div className="flex items-center gap-2 ">
                  <span>{t("text.scrollToRead")}</span>
                  <ChevronDown className="w-4 h-4" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div
          className="flex-1 p-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          onScroll={handleScroll}
          ref={scrollAreaRef}
        >
          {cguPdf &&
            <div className="relative min-h-[600px] overflow-x-hidden bg-white border border-gray-200 rounded-lg ">
              <Document file={cgu.url} onLoadSuccess={onDocumentLoadSuccess}>
                {Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={documentWidth} // ajuste la largeur selon ton design
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ))}
              </Document>
            </div>
          }
        </div>


        {/* Bottom gradient overlay when not at bottom */}
        {!isAtBottom && (
          <div className="absolute bottom-20 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        )}

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <Button
            dataId="download-pdf"
            variant="outline"
            className="flex items-center gap-2 bg-transparent"
            onClick={downloadCgu}
          >
            <Download className="w-4 h-4" />
            {t("text.downloadPDF")}
          </Button>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            {!isAtBottom && <p className="text-sm text-gray-500">{t("text.readAllDocument")}</p>}
            <Button
              dataId="accept-cgus"
              onClick={() => onAccept(cgu.id)}
              disabled={!isAtBottom}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed px-8"
            >
              {isAtBottom ? t("button.accept") : t("button.readAllDocument")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
